import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Axios from "axios";
import { rectSortingStrategy } from "@dnd-kit/sortable";
import { MultipleContainers } from "./MultipleContainersComponent";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../../../loaders/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export default function PageDesign() {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const containersRef = useRef([]);
  const itemsRef = useRef({});

  const [containerData, setContainerData] = useState({});
  const [lineOfBusiness, setLineOfBusiness] = useState([]);
  const [selectedLob, setSelectedLob] = useState(null);
  const [selectedOption, setSelectedOption] = useState("Select a page");
  const [inputFieldsData, setInputFieldsData] = useState([]);
  const [originalInputFields, setOriginalInputFields] = useState([]);
  const [items, setItems] = useState({ E: [] });

  const fetchInputFields = async () => {
    const url = `policy/inputfield?count=true&$filter=programId eq ${id} and isDeleted eq false`;

    try {
      const token = await getAccessTokenSilently();
      const response = await Axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        const filteredData = response.data
          .filter((item) => !item.isDeleted)
          .map((item, index) => ({
            ...item,
            name: item.name || `Input Field ${index + 1}`,
          }));
        setInputFieldsData(filteredData);
        setOriginalInputFields(filteredData);
        setItems({ E: filteredData });
        return filteredData;
      } else {
        throw new Error("No data returned from the API");
      }
    } catch (error) {
      if (Axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error("Error fetching input fields:", error);
      }
      return [];
    }
  };

  const fetchLineOfBusiness = async () => {
    const url = `policy/lineofbusiness?count=true&$filter=programId eq ${id} and isDeleted eq false`;

    try {
      const token = await getAccessTokenSilently();
      const response = await Axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        return response.data;
      } else {
        throw new Error("No data returned from the API");
      }
    } catch (error) {
      console.error("Error fetching line of business:", error);
      return [];
    }
  };

  const fetchPageDesign = useCallback(async () => {
    if (!selectedLob) return null;

    const lookupKey = selectedLob.name;
    const url = `policy/pageDesign?$filter=lookupKey eq '${lookupKey}'`;

    try {
      const token = await getAccessTokenSilently();
      const response = await Axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.length > 0) {
        const pageDesigns = response.data;
        const mostRecentEntry = pageDesigns[pageDesigns.length - 1];
        const parsedData = JSON.parse(mostRecentEntry.data);
        return parsedData;
      } else {
        console.error("No data returned from the API or data is empty");
        return null;
      }
    } catch (error) {
      console.error("Error fetching page design:", error);
      return null;
    }
  }, [getAccessTokenSilently, selectedLob]);

  const { data: inputFields, isLoading: inputFieldsLoading } = useQuery(
    ["inputField", id],
    fetchInputFields,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: lobData, isLoading: lobLoading } = useQuery(
    ["lineOfBusiness", id],
    fetchLineOfBusiness,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (lobData) {
      setLineOfBusiness(lobData);
    }
  }, [lobData]);

  useEffect(() => {
    const initializeContainerData = async () => {
      if (selectedLob) {
        const mostRecentPageDesign = await fetchPageDesign();
        if (mostRecentPageDesign) {
          const { display, inputFields: inputFieldsMap } = mostRecentPageDesign;

          const crossReferencedData = display.map((container, index) => ({
            container: container.title || `Container ${index + 1}`,
            fields: container.fields.map((fieldId) => ({
              id: fieldId,
              name: inputFieldsMap[fieldId]?.name || `Field ${fieldId}`,
              width: inputFieldsMap[fieldId]?.width || 1,
              requiredToQuote:
                inputFieldsMap[fieldId]?.required?.quote || false,
              ...inputFieldsMap[fieldId],
            })),
          }));

          console.log("Cross-referenced container data:", crossReferencedData);

          const newContainerData = crossReferencedData.reduce(
            (acc, container, index) => {
              const containerId = `Container${index + 1}`;
              acc[containerId] = {
                id: containerId,
                title: container.container,
                collapsible: container.collapsible || false,
                type: container.type || "default",
                width: container.width || 1,
              };
              return acc;
            },
            {}
          );
          console.log("New Container Data:", newContainerData);
          setContainerData(newContainerData);

          const newItems = crossReferencedData.reduce(
            (acc, container, index) => {
              const containerId = `Container${index + 1}`;
              acc[containerId] = container.fields;
              return acc;
            },
            { E: inputFieldsData }
          );
          console.log("New Items:", newItems);

          const usedFieldIds = crossReferencedData.flatMap((container) =>
            container.fields.map((field) => field.id)
          );
          const availableInputFields = inputFieldsData.filter(
            (field) => !usedFieldIds.includes(field.id)
          );

          setItems({ ...newItems, E: availableInputFields });
          containersRef.current = Object.keys(newContainerData);
          itemsRef.current = { ...newItems, E: availableInputFields };
        } else {
          console.log("No page design data found for selected LOB");
          setContainerData({});
          setItems({ E: inputFieldsData });
        }
      }
    };

    initializeContainerData();
  }, [selectedLob, inputFieldsData, fetchPageDesign]);

  const handleSave = async (containers, items) => {
    if (!Array.isArray(containers) || typeof items !== "object") {
      console.error("Invalid containers or items structure");
      return;
    }

    if (!selectedLob) {
      console.error("No line of business selected");
      return;
    }

    const usedItems = containers.reduce((acc, containerId) => {
      if (items[containerId]) {
        acc.push(...items[containerId]);
      }
      return acc;
    }, []);

    const defaultItemFields = (item) => ({
      name: item.name || "",
      order: item.order || 1,
      width: item.width || 1,
      placeholderText: item.placeholderText || "",
      tooltip: item.tooltip || "",
      displayFormat: item.displayFormat || "",
      required: item.required || {
        quote: true,
        bind: false,
      },
      disabled: item.disabled || false,
      attachToExposure: item.attachToExposure || false,
      canOverride: item.canOverride || false,
    });

    const payload = {
      name: `PageDesign-${id}`,
      lookupKey: selectedLob.name,
      programId: id,
      data: JSON.stringify({
        display: containers.map((containerId, index) => ({
          type: containerData[containerId]?.type || "default",
          title: containerData[containerId]?.title || "",
          fields: items[containerId].map((item) => item.id),
          isCollapsible: containerData[containerId]?.collapsible || false,
          width: containerData[containerId]?.width || 100,
          order: index + 1,
        })),
        inputFields: usedItems.reduce((acc, item, index) => {
          acc[item.id] = {
            ...defaultItemFields(item),
            order: index + 1,
          };
          return acc;
        }, {}),
      }),
    };

    console.log(JSON.stringify(payload, null, 2));

    try {
      const token = await getAccessTokenSilently();
      const url = "policy/pageDesign";

      const response = await Axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Save successful:", response.data);
      toast.success("Save successful!");
    } catch (error) {
      if (error.response) {
        console.error("Error saving page design:", error.response.data);
      } else if (error.request) {
        console.error("Error saving page design:", error.request);
      } else {
        console.error("Error saving page design:", error.message);
      }
      toast.error("Error saving page design.");
    }
  };

  const handleUpdateContainerData = (id, data) => {
    setContainerData((prevData) => ({
      ...prevData,
      [id]: {
        ...prevData[id],
        ...data,
      },
    }));
  };

  const handleSaveItem = (containerId, updatedItem) => {
    setItems((prevItems) => {
      const updatedContainerItems = prevItems[containerId].map((item) =>
        item.id === updatedItem.id ? updatedItem : item
      );

      return {
        ...prevItems,
        [containerId]: updatedContainerItems,
      };
    });
  };

  const handleRemoveItem = (containerId, itemId) => {
    setItems((items) => {
      const removedItem = items[containerId].find((item) => item.id === itemId);
      return {
        ...items,
        [containerId]: items[containerId].filter((item) => item.id !== itemId),
        E: [resetItemToOriginal(removedItem), ...items["E"]],
      };
    });
  };

  const resetItemToOriginal = (item) => {
    const originalItem = originalInputFields.find(
      (original) => original.id === item.id
    );
    return originalItem ? { ...originalItem } : item;
  };

  const handleAddInputToContainer = (containerId, input) => {
    setItems((prevItems) => {
      const updatedItems = { ...prevItems };
      updatedItems[containerId] = [...updatedItems[containerId], input];
      updatedItems["E"] = updatedItems["E"].filter(
        (item) => item.id !== input.id
      );
      return updatedItems;
    });
  };

  const getNextContainerId = () => {
    const containerIds = Object.keys(containerData);
    const numericIds = containerIds
      .map((id) => parseInt(id.replace("Container", ""), 10))
      .filter((num) => !isNaN(num));
    const nextId = Math.max(0, ...numericIds) + 1;
    return `Container${nextId}`;
  };

  const handleRemoveContainer = (containerID) => {
    setContainerData((containers) => {
      const newContainers = { ...containers };
      delete newContainers[containerID];
      return newContainers;
    });

    setItems((items) => {
      const containerItems = items[containerID] || [];
      return {
        ...items,
        E: [
          ...containerItems.map((item) => resetItemToOriginal(item)),
          ...items["E"],
        ],
        [containerID]: [],
      };
    });
  };

  if (inputFieldsLoading || lobLoading) return <Loader />;

  console.log("Rendered Container Data:", containerData);
  console.log("Rendered Items:", items);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-5">
      <div className="flex max-md:flex-col justify-between items-center gap-x-5 gap-y-2.5 mb-5">
        <div className="w-full">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            Page Designer
          </h2>
          <span className="text-sm text-zinc-500 dark:text-zinc-400 font-light">
            Control the page layout, sorting, input field display styles, and
            more.
          </span>
        </div>
        <div className="flex justify-between md:justify-end items-center gap-x-1 w-full">
          <Menu
            as="div"
            className="relative inline-block text-left w-full max-w-xs"
          >
            <div>
              <Menu.Button className="inline-flex w-full justify-between gap-x-1.5 rounded-md bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm ring-1 ring-inset ring-zinc-300 dark:ring-zinc-700">
                {selectedOption}
                <ChevronDownIcon
                  aria-hidden="true"
                  className="-mr-1 h-5 w-5 text-zinc-400"
                />
              </Menu.Button>
            </div>
            <Menu.Items className="absolute right-0 z-10 mt-2 w-full max-w-xs origin-top-right rounded-md bg-white dark:bg-zinc-800 shadow-lg ring-1 ring-zinc-300 dark:ring-zinc-700 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in">
              <div className="py-1">
                <Menu.Item>
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100"
                    onClick={() => {
                      setSelectedOption("Insured");
                      setSelectedLob({ name: "Insured" });
                    }}
                  >
                    Insured
                  </button>
                </Menu.Item>
                {lineOfBusiness.map((lob) => (
                  <Menu.Item key={lob.id}>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100"
                      onClick={() => {
                        setSelectedOption(lob.name);
                        setSelectedLob(lob);
                      }}
                    >
                      {lob.name}
                    </button>
                  </Menu.Item>
                ))}
                <Menu.Item>
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100"
                    onClick={() => {
                      setSelectedOption("Policy");
                      setSelectedLob({ name: "Policy" });
                    }}
                  >
                    Policy
                  </button>
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
          <div className="flex gap-x-1">
            <button className="inline-flex items-center gap-x-1 rounded-md bg-zinc-800 px-4 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-zinc-300 dark:ring-zinc-700 hover:bg-zinc-700 focus:z-10 whitespace-nowrap">
              Preview
            </button>
            <button
              className="inline-flex items-center gap-x-1 rounded-md bg-[#4d7c0f]/80 hover:bg-[#4d7c0f] px-4 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-zinc-300 dark:ring-zinc-700 focus:z-10 whitespace-nowrap"
              onClick={() =>
                handleSave(containersRef.current, itemsRef.current)
              }
            >
              Save
            </button>
          </div>
        </div>
      </div>
      <MultipleContainers
        columns={3}
        itemCount={5}
        strategy={rectSortingStrategy}
        wrapperStyle={() => ({
          width: "auto",
          height: "auto",
        })}
        vertical
        inputFields={inputFields}
        items={items}
        setItems={setItems}
        onSave={(containers, items) => {
          containersRef.current = containers;
          itemsRef.current = items;
        }}
        setContainerData={setContainerData}
        containerData={containerData}
        onUpdateContainerData={handleUpdateContainerData}
        handleSaveItem={handleSaveItem}
        handleRemoveItem={handleRemoveItem}
        handleAddInputToContainer={handleAddInputToContainer}
        getNextContainerId={getNextContainerId}
        handleRemoveContainer={handleRemoveContainer}
      />
    </div>
  );
}
